/**
 * Une identité unique et stable pour les sites Matomo et Piano.
 *
 * ## Le défaut que ce module referme
 *
 * Le même site était décrit par **deux endpoints qui ne donnaient pas le même sens à `id`** :
 *
 * | Champ | `/api/library` | `/api/dashboard/{matomo,piano}/sites` |
 * |---|---|---|
 * | `id`  | le numéro de site (`'2'`) | l'identifiant de base (cuid) |
 * | `idSite` | `null` | le numéro |
 *
 * Les deux écrivaient dans le **même état React**, `matomoSites` : `App.js` au chargement de la
 * bibliothèque, le tableau de bord à son montage. Selon celui qui avait écrit en dernier,
 * `site.id` valait `'2'` ou `'cmmuqu4q2…'`, et tout ce qui s'y appuie basculait avec lui — le
 * site actif dans le menu, la vérification « ce site est-il encore valide ? », l'auto-sélection,
 * et jusqu'au paramètre envoyé à l'API.
 *
 * Deux conséquences, qui se lisaient comme de l'instabilité :
 *
 * 1. **La route de données attend le cuid** (`findFirst({ id: siteId })`). Sélectionner un site
 *    alors que la liste venait de la bibliothèque envoyait `'2'` : aucun `LibraryItem` ne porte
 *    cet identifiant, et le tableau restait vide. Le même geste, après un passage par le tableau
 *    de bord, fonctionnait.
 * 2. **Le numéro de site n'est unique que dans une instance.** `idSite = 2` désigne « Espace
 *    Locataires » chez un client et « Paris en Scène » chez un autre : deux sites distincts
 *    partageaient la même clé React et le même identifiant de sélection.
 *
 * ## La règle
 *
 * L'identifiant de base fait foi — il est unique, et c'est celui que le backend attend. Le numéro
 * reste disponible sous `idSite` et `sourceId`, dont les rattachements ont besoin.
 *
 * Normaliser **aux deux entrées** est le point essentiel : ne le faire qu'à une seule laisserait
 * exactement le désaccord qu'on vient de décrire.
 */

/**
 * Ramène un site de mesure à sa forme canonique.
 *
 * @param {object} site - tel que le rend `/api/library` ou la route `sites` d'un tableau de bord
 * @returns {object} le même site, dont `id` est l'identifiant de base et `idSite` le numéro
 */
export const normaliserSiteDeMesure = (site) => {
  if (!site || typeof site !== 'object') return site;

  // `databaseId` n'existe que dans la charge de `/api/library` ; ailleurs, `id` est déjà le bon.
  const identifiant = site.databaseId || site.id;
  // Le numéro se lit d'abord là où les deux sources le placent sûrement.
  const numero = site.idSite ?? site.sourceId ?? null;

  return {
    ...site,
    id: identifiant,
    databaseId: identifiant,
    idSite: numero,
    sourceId: site.sourceId ?? (numero == null ? null : String(numero))
  };
};

/** La même chose sur une liste, en tolérant une valeur qui n'en serait pas une. */
export const normaliserSitesDeMesure = (sites) => (
  Array.isArray(sites) ? sites.map(normaliserSiteDeMesure) : []
);

/**
 * Les trois écritures sous lesquelles un rattachement Matomo ou Piano a pu être enregistré.
 *
 * Relevé en production : **tous** les rattachements Matomo du parc sont encore des numéros nus
 * — « 3 », « 2 », « 4 » —, écrits avant que le format porte la connexion. La bibliothèque ne
 * testait que le format récent : elle affichait « Aucun site associé » sur des comptes pourtant
 * rattachés, et aucun site n'apparaissait « ✓ Associé » dans le menu, si bien qu'on ne pouvait
 * même pas défaire ce qui existait.
 *
 * Le numéro nu n'est retenu que lorsqu'il ne désigne qu'un seul site du parc. `idSite = 4`
 * valant à la fois « capitaine fracasse » et « App Android », le retenir des deux côtés
 * afficherait un rattachement là où il n'y en a pas — plus trompeur que de n'en afficher aucun.
 */
export const fabriquerResolveurDeRattachement = (prefixe, sites, champsNumero = ['sourceId', 'idSite']) => {
  // Le numéro ne se lit pas sous le même nom partout : `idSite` chez Matomo, `siteId` chez Piano.
  const numeroDe = (site) => {
    for (const champ of champsNumero) {
      const n = site?.[champ];
      if (n != null && n !== '') return String(n);
    }
    return '';
  };

  const ambigus = (() => {
    const compte = new Map();
    for (const site of sites || []) {
      const n = numeroDe(site);
      if (n) compte.set(n, (compte.get(n) || 0) + 1);
    }
    return new Set([...compte.entries()].filter(([, n]) => n > 1).map(([n]) => n));
  })();

  const identifiantDe = (site) => `${prefixe}-${site?.connectionId || 'default'}-${numeroDe(site)}`;

  return {
    identifiantDe,
    numeroDe,
    /** Ce numéro désigne-t-il plusieurs sites du parc ? */
    estAmbigu: (site) => ambigus.has(numeroDe(site)),
    /** Ce rattachement enregistré désigne-t-il ce site ? */
    correspond: (site, accountId) => {
      const brut = String(accountId);
      if (brut === identifiantDe(site)) return true;
      // Format intermédiaire, bâti sur l'identifiant de base plutôt que sur le numéro.
      if (brut === `${prefixe}-${site?.connectionId || 'default'}-${site?.databaseId || site?.id}`) return true;
      const numero = numeroDe(site);
      if (!numero) return false;
      // Numéro nu : accepté seulement s'il ne peut désigner que ce site.
      return brut === numero && !ambigus.has(numero);
    },
    /**
     * Les rattachements anciens que l'écriture du format récent rend caducs.
     *
     * Associer explicitement un site, c'est précisément déclarer laquelle des instances le
     * numéro nu désignait : le garder à côté laisserait l'ambiguïté en place pour toujours.
     */
    heritesRemplaces: (site, accountIds) => {
      const numero = numeroDe(site);
      if (!numero) return [];
      return (accountIds || []).filter(a => String(a) === numero);
    }
  };
};
